import type { Transaction, TransactionRefundLink } from '@/types';

// Pure cache-patch helpers for refund mutations. The link/unlink endpoints do
// NOT return updated transactions, so the screen patches its SWR pages locally
// from what it already knows and lets a background revalidation confirm.

export interface RefundPairSide {
  readonly id: string;
  readonly description: string;
  readonly merchantName?: string | null;
  /** Signed BRL reporting amount — only the magnitude is used. */
  readonly amount: number;
  readonly date: string;
  readonly accountName: string;
}

export interface RefundLinkPatchInput {
  readonly linkId: string;
  readonly linkedAmount: number;
  readonly createdAt: string;
  readonly expense: RefundPairSide;
  readonly refund: RefundPairSide;
}

function toCounterpartLink(
  input: RefundLinkPatchInput,
  role: 'expense' | 'refund',
): TransactionRefundLink {
  const counterpart = role === 'expense' ? input.refund : input.expense;
  return {
    linkId: input.linkId,
    role,
    counterpartTransactionId: counterpart.id,
    counterpartDescription: counterpart.description,
    counterpartMerchantName: counterpart.merchantName ?? undefined,
    counterpartAmount: counterpart.amount,
    counterpartDate: counterpart.date,
    counterpartAccountName: counterpart.accountName,
    linkedAmount: input.linkedAmount,
    createdAt: input.createdAt,
  };
}

/** Appends a link to a row's set, keeping `refundLink` pointing at the first one. */
function withLinkAppended(
  tx: Transaction,
  link: TransactionRefundLink,
  linkedAmount: number,
): Transaction {
  const links = [...(tx.refundLinks ?? (tx.refundLink ? [tx.refundLink] : [])), link];
  return {
    ...tx,
    refundedAmount: (tx.refundedAmount ?? 0) + linkedAmount,
    refundLink: links[0],
    refundLinks: links,
  };
}

/**
 * Stamps a fresh link onto both rows of the pair (whichever of them are in the
 * cached list). A refund link is an ANNOTATION — it never changes a row's
 * `isCashFlowNeutral`, so the patch leaves that untouched (the only surface a
 * link affects, "Ritmo de gastos", is server-side). Linking always clears a
 * leftover "previsto" flag on the expense.
 *
 * ADDITIVE on both sides: an expense can already carry earlier partial estornos
 * (and an estorno earlier parcelas), so overwriting would drop them from the cache
 * until the next revalidation.
 */
export function applyRefundLinkPatch(
  transactions: readonly Transaction[],
  input: RefundLinkPatchInput,
): Transaction[] {
  return transactions.map((tx) => {
    if (tx.id === input.expense.id) {
      return {
        ...withLinkAppended(tx, toCounterpartLink(input, 'expense'), input.linkedAmount),
        expectedRefundFlaggedAt: null,
        expectedRefundExpiredAt: null,
      };
    }
    if (tx.id === input.refund.id) {
      return withLinkAppended(tx, toCounterpartLink(input, 'refund'), input.linkedAmount);
    }
    return tx;
  });
}

/**
 * Removes ONE link from both rows that carry it, leaving the row's other links intact
 * (an expense may still be covered by other estornos). A link never touched neutrality,
 * so only the descriptive fields change. Nothing else (flag, category) is restored —
 * unlink never re-enters review.
 */
export function clearRefundLinkPatch(
  transactions: readonly Transaction[],
  linkId: string,
): Transaction[] {
  return transactions.map((tx) => {
    const current = tx.refundLinks ?? (tx.refundLink ? [tx.refundLink] : []);
    const removed = current.find((l) => l.linkId === linkId);
    if (!removed) return tx;
    const remaining = current.filter((l) => l.linkId !== linkId);
    return {
      ...tx,
      refundedAmount: Math.max(0, (tx.refundedAmount ?? removed.linkedAmount) - removed.linkedAmount),
      refundLink: remaining[0] ?? null,
      refundLinks: remaining.length > 0 ? remaining : null,
    };
  });
}

/**
 * Applies a cache patch to one standalone row. The detail drawer can hold a
 * transaction fetched via getOne that lives in no SWR page — pages-only patches
 * would leave that fallback stale forever, since revalidation cannot heal a row
 * the pages never contained. Returns the same instance when the patch didn't
 * touch the row, so state setters can skip no-op updates.
 */
export function patchStandaloneTransaction<T>(
  current: T | null,
  getSource: (value: T) => Transaction,
  patch: (rows: Transaction[]) => Transaction[],
  readapt: (source: Transaction) => T,
): T | null {
  if (!current) return null;
  const source = getSource(current);
  const [patched] = patch([source]);
  if (!patched || patched === source) return current;
  return readapt(patched);
}

/**
 * The affected ids a standalone holder (detail drawer / its origin stack)
 * currently displays — the rows worth refetching authoritatively after a
 * mutation whose result the client cannot fully recompute (base neutrality
 * from custom-kind categories is server-side). Deduped; nullish-tolerant.
 */
export function heldAffectedIds(
  affectedIds: readonly (string | null | undefined)[],
  heldIds: readonly (string | null | undefined)[],
): string[] {
  const held = new Set(heldIds.filter((id): id is string => !!id));
  return Array.from(new Set(affectedIds.filter((id): id is string => !!id && held.has(id))));
}

/** Toggles the "reembolso previsto" flag on one row. The flag is a Ritmo-only
 * hint and never changes the row's neutrality, so `isCashFlowNeutral` is left as-is. */
export function applyExpectedRefundPatch(
  transactions: readonly Transaction[],
  transactionId: string,
  flaggedAt: string | null,
): Transaction[] {
  return transactions.map((tx) => {
    if (tx.id !== transactionId) return tx;
    return {
      ...tx,
      expectedRefundFlaggedAt: flaggedAt,
      expectedRefundExpiredAt: null,
    };
  });
}
